/**
 * PR Trajectory Engine
 *
 * Analyzes weight progression per exercise and predicts how many sessions
 * until the user hits a new PR. Uses linear regression over recent sessions.
 *
 * All calculations are instant — no API calls.
 */

/**
 * Returns the max weight used per session for an exercise, sorted oldest → newest.
 * Only counts completed sets.
 */
export function getExerciseWeightHistory(exerciseId, workouts) {
  return [...workouts]
    .sort((a, b) => a.startTime - b.startTime)
    .reduce((acc, w) => {
      const block = (w.exerciseBlocks || []).find((b) => b.exerciseId === exerciseId);
      if (!block) return acc;
      const weights = (block.sets || [])
        .filter((s) => s.done)
        .map((s) => parseFloat(s.weight) || 0)
        .filter((w) => w > 0);
      if (weights.length > 0) {
        acc.push({ date: w.startTime, maxWeight: Math.max(...weights) });
      }
      return acc;
    }, []);
}

/**
 * Computes a linear regression slope over an array of { maxWeight } sessions.
 * Returns { slope, intercept, r2 } or null if computation is invalid.
 *
 * @param {Array<{maxWeight: number}>} sessions - ordered oldest → newest
 */
function linearRegression(sessions) {
  const n = sessions.length;
  if (n < 2) return null;

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  sessions.forEach((s, i) => {
    sumX += i;
    sumY += s.maxWeight;
    sumXY += i * s.maxWeight;
    sumX2 += i * i;
  });

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  let ssTot = 0, ssRes = 0;
  sessions.forEach((s, i) => {
    ssTot += (s.maxWeight - meanY) ** 2;
    ssRes += (s.maxWeight - (slope * i + intercept)) ** 2;
  });
  const r2 = ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0;

  return { slope, intercept, r2 };
}

/**
 * Calculates the PR trajectory for a single exercise.
 *
 * Returns null if:
 * - Not enough data (< 2 sessions)
 * - Already at or exceeding the PR
 * - Not progressing (slope < 0.5 lbs/session)
 * - PR is too far away (> 15 sessions) — not useful to show
 *
 * Returns:
 * @typedef {Object} Trajectory
 * @property {number} sessionsToGo - estimated sessions until PR
 * @property {number} ratePerSession - avg lbs gained per session
 * @property {number} lastMaxWeight - heaviest weight in most recent session
 * @property {number} prWeight - current PR weight
 * @property {number} weightGap - lbs between lastMaxWeight and PR
 * @property {number} confidence - R² (0–1, higher = more consistent trend)
 * @property {number} sessionCount - total sessions used
 */
export function calculateTrajectory(exerciseId, personalRecords, workouts) {
  const pr = personalRecords[exerciseId];
  if (!pr?.weight) return null;

  const history = getExerciseWeightHistory(exerciseId, workouts);
  if (history.length < 2) return null;

  // Use the most recent 8 sessions for the trend (more data = noisier if old)
  const recent = history.slice(-8);
  const lastMaxWeight = recent[recent.length - 1].maxWeight;

  // Already matched or exceeded PR — nothing to forecast
  if (lastMaxWeight >= pr.weight) return null;

  const reg = linearRegression(recent);
  if (!reg || reg.slope < 0.5) return null; // < 0.5 lbs/session is noise

  const weightGap = pr.weight - lastMaxWeight;
  const sessionsToGo = Math.ceil(weightGap / reg.slope);

  if (sessionsToGo > 15) return null; // Too speculative

  return {
    sessionsToGo,
    ratePerSession: Math.round(reg.slope * 10) / 10,
    lastMaxWeight,
    prWeight: pr.weight,
    weightGap: Math.round(weightGap * 10) / 10,
    confidence: Math.round(reg.r2 * 100) / 100,
    sessionCount: history.length,
  };
}

/**
 * Returns the top N exercises approaching a PR, sorted by sessions to go (ascending).
 * Excludes exercises with < 0.4 R² (noisy trends).
 *
 * @param {Object} personalRecords - { [exerciseId]: { weight, reps, date } }
 * @param {Array}  workouts
 * @param {Array}  exercises - exercise library with { id, name, category }
 * @param {number} limit
 * @returns {Array<{ exerciseId, exerciseName, ...Trajectory }>}
 */
export function getTopTrajectories(personalRecords, workouts, exercises, limit = 3) {
  const results = [];

  for (const [exId] of Object.entries(personalRecords)) {
    const traj = calculateTrajectory(exId, personalRecords, workouts);
    if (!traj) continue;
    if (traj.confidence < 0.25) continue; // Require at least some linearity
    const ex = (exercises || []).find((e) => e.id === exId);
    if (!ex) continue;
    results.push({ exerciseId: exId, exerciseName: ex.name, ...traj });
  }

  return results
    .sort((a, b) => a.sessionsToGo - b.sessionsToGo || b.confidence - a.confidence)
    .slice(0, limit);
}

/**
 * Returns trajectories for all exercises currently in the workout.
 * Used in ActiveWorkout to show per-exercise hints.
 *
 * @returns {Object} { [exerciseId]: Trajectory }
 */
export function getWorkoutTrajectories(exerciseBlocks, personalRecords, workouts) {
  const result = {};
  for (const block of exerciseBlocks || []) {
    const traj = calculateTrajectory(block.exerciseId, personalRecords, workouts);
    if (traj) result[block.exerciseId] = traj;
  }
  return result;
}

/**
 * Projects the NEXT weight milestone after a new PR is set.
 * Used in PRCelebration to show "at this pace, you'll hit X lbs in ~Y sessions."
 *
 * @param {string} exerciseId
 * @param {number} newPRWeight - the PR just achieved
 * @param {Array}  workouts
 * @returns {{ projectedWeight, sessionsAway, ratePerSession } | null}
 */
export function getNextMilestone(exerciseId, newPRWeight, workouts) {
  const history = getExerciseWeightHistory(exerciseId, workouts);
  if (history.length < 2) return null;

  const recent = history.slice(-8);
  const reg = linearRegression(recent);
  if (!reg || reg.slope < 0.5) return null;

  // Project forward: what round-number milestone is reachable in 2–5 sessions?
  const projected3 = newPRWeight + reg.slope * 3;
  // Round up to nearest 5 lbs milestone
  const projectedWeight = Math.ceil(projected3 / 5) * 5;
  const sessionsAway = Math.max(2, Math.round((projectedWeight - newPRWeight) / reg.slope));

  if (sessionsAway > 10) return null;

  return {
    projectedWeight,
    sessionsAway,
    ratePerSession: Math.round(reg.slope * 10) / 10,
  };
}
